/**
 * Modell configurator custom element (<modell-configurator>).
 *
 * Moved out of a snippet-level {% javascript %} tag into a real asset file:
 * the local theme dev bundler for snippet-scoped {% javascript %} tags
 * (snippet-scripts.js) was truncating output for this file regardless of
 * its content, while section-scoped {% javascript %} bundling (scripts.js)
 * worked fine. Using a plain <script src> tag (like variant-picker.js)
 * sidesteps that bundling path entirely.
 */
if (!customElements.get('modell-configurator')) {
  /**
   * URL hash the configurator writes its open step into, e.g.
   * `#konfigurator-2` for the second step. Index-based rather than named
   * after the step's taxonomy, because a bundle_config may well contain two
   * steps of the same taxonomy (e.g. two Zubehör steps).
   */
  const HISTORY_HASH_PREFIX = 'konfigurator-';

  class ModellConfigurator extends HTMLElement {
    connectedCallback() {
      this.initState();
      this.trigger = this.querySelector('[data-modell-configurator-trigger]');
      this.closeButton = this.querySelector('[data-modell-configurator-close]');
      this.panel = this.querySelector('[data-modell-configurator-panel]');
      this.track = this.querySelector('[data-modell-configurator-track]');
      this.viewport = this.querySelector(
        '[data-modell-configurator-viewport]',
      );

      // The step track slides via translateX inside the overflow:hidden
      // viewport - but browsers ALSO scroll overflow:hidden containers
      // programmatically (focus()/scrollIntoView() on an element inside an
      // off-screen step panel, e.g. keyboard-tabbing into another step's
      // inputs or a browser find-in-page hit), which sets a persistent
      // scrollLeft on top of the transform and permanently misaligns the
      // panels. Pin it back to 0 whenever that happens.
      if (this.viewport) {
        this.viewport.addEventListener('scroll', () => {
          if (this.viewport.scrollLeft !== 0) this.viewport.scrollLeft = 0;
        });
      }
      this.steps = Array.from(
        this.querySelectorAll('[data-modell-configurator-step-panel]'),
      );
      this.stepperItems = Array.from(
        this.querySelectorAll('[data-modell-configurator-step]'),
      );

      this.groups = Array.from(
        this.querySelectorAll('[data-modell-configurator-group]'),
      );
      this.quantityLists = Array.from(
        this.querySelectorAll('[data-modell-configurator-quantity-list]'),
      );
      this.accessories = Array.from(
        this.querySelectorAll('[data-modell-configurator-accessory]'),
      );
      this.summaryContainer = this.querySelector(
        '[data-modell-configurator-summary]',
      );
      this.totalEl = this.querySelector('[data-modell-configurator-total]');
      this.totalValueEl = this.querySelector(
        '[data-modell-configurator-total-value]',
      );

      this.currentIndex = 0;
      this.maxUnlockedIndex = 0;

      // Not `this.open.bind(this)`: open() takes an options object, and the
      // click event would be read as one.
      this.trigger.addEventListener('click', () => this.open());
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.close());
      }

      this.querySelectorAll('[data-modell-configurator-prev]').forEach(
        (button) => {
          button.addEventListener('click', this.goToPrevious.bind(this));
        },
      );

      this.querySelectorAll('[data-modell-configurator-next]').forEach(
        (button) => {
          button.addEventListener('click', this.goToNext.bind(this));
        },
      );

      this.submitButton = this.querySelector(
        '[data-modell-configurator-submit]',
      );
      this.submitError = this.querySelector(
        '[data-modell-configurator-submit-error]',
      );
      if (this.submitButton) {
        this.submitButton.addEventListener(
          'click',
          this.handleSubmit.bind(this),
        );
      }

      this.stepperItems.forEach((item) => {
        item.addEventListener('click', () => {
          const index = Number(item.dataset.stepIndex);
          if (index <= this.maxUnlockedIndex) this.goToStep(index);
        });
      });

      this.querySelectorAll('[data-modell-configurator-option]').forEach(
        (input) => {
          input.addEventListener('change', this.handleOptionChange.bind(this));
        },
      );

      this.querySelectorAll('[data-modell-configurator-size]').forEach(
        (input) => {
          input.addEventListener('change', this.handleSizeChange.bind(this));
        },
      );

      this.querySelectorAll(
        '[data-modell-configurator-variant-select], [data-modell-configurator-variant-swatch]',
      ).forEach((control) => {
        control.addEventListener(
          'change',
          this.handleVariantOptionChange.bind(this),
        );
      });

      this.querySelectorAll(
        '[data-modell-configurator-accessory-toggle]',
      ).forEach((toggle) => {
        toggle.addEventListener(
          'change',
          this.handleAccessoryToggleChange.bind(this),
        );
      });

      // The whole accessory row is one selectable line: a click anywhere in
      // it toggles the accessory. Native controls are skipped - the card is
      // a real <label for>, the swatches are labels, the stepper is
      // buttons/anchors around an input - so those keep their own behaviour
      // (and a label click would otherwise be undone by toggling twice).
      this.addEventListener('click', (event) => {
        const row = event.target.closest(
          '.modell-configurator__accessory-row',
        );
        if (!row) return;
        if (
          event.target.closest(
            'label, input, select, button, a, [data-modell-configurator-quantity-wrapper]',
          )
        ) {
          return;
        }
        const toggle = row.querySelector(
          '[data-modell-configurator-accessory-toggle]',
        );
        if (!toggle) return;
        toggle.checked = !toggle.checked;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.addEventListener('click', (event) => {
        const decrementButton = event.target.closest(
          '[data-modell-configurator-quantity-decrement]',
        );
        const incrementButton = event.target.closest(
          '[data-modell-configurator-quantity-increment]',
        );
        if (!decrementButton && !incrementButton) return;
        // the +/- controls are <a href="#"> (PDP buttoned-input markup)
        event.preventDefault();

        const row = event.target.closest(
          '[data-modell-configurator-quantity-row]',
        );
        const input = row
          ? row.querySelector('[data-modell-configurator-quantity]')
          : null;
        if (!input) return;

        const min = Number(input.min) || 1;
        const step = decrementButton ? -1 : 1;
        input.value = Math.max(min, (Number(input.value) || min) + step);
        this.setQtyOverride(
          row.dataset.entryId,
          this.currentSize(),
          Number(input.value),
        );
        this.updateSummary();
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-modell-configurator-quantity]')) {
          const row = event.target.closest(
            '[data-modell-configurator-quantity-row]',
          );
          if (row) {
            this.setQtyOverride(
              row.dataset.entryId,
              this.currentSize(),
              Number(event.target.value),
            );
          }
          this.updateSummary();
        }
      });

      this.querySelectorAll('[data-modell-configurator-reset-groups]').forEach(
        (button) => {
          button.addEventListener('click', () => {
            this.resetGroups(button.dataset.modellConfiguratorResetGroups);
          });
        },
      );

      this.querySelectorAll(
        '[data-modell-configurator-reset-quantities]',
      ).forEach((button) => {
        button.addEventListener('click', () => this.resetQuantityOverrides());
      });

      this.groups.forEach((group) => {
        this.updateSelectedClasses(group);
      });

      this.querySelectorAll('[data-modell-configurator-swatches]').forEach(
        (container) => this.initSwatchPagination(container),
      );

      this.querySelectorAll(
        '.modell-configurator__variant-select-wrapper--grow',
      ).forEach((wrapper) => this.updatePickerTitle(wrapper));

      this.accessories.forEach((accessory) =>
        this.syncAccessoryThumb(accessory),
      );

      // Initial "Weiter" state per step: disabled while the (server-
      // preselected) selection contains a sold-out variant.
      this.steps.forEach((panel) => this.updateNextButtonState(panel));

      this.renderStep();
      this.renderQuantityRows();

      this.initHistory();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    /* ---------------------------------------------------------------
       Browser history
       Each open step is one history entry (`#konfigurator-<n>`), so the
       browser's back/forward buttons (and the Android back gesture) step
       through the configurator instead of leaving the product page. The
       hash - not history.state - is the source of truth, so a reload or a
       shared link lands on the same step.
       --------------------------------------------------------------- */

    initHistory() {
      // Step index the CURRENT history entry represents; null = configurator
      // closed (intro).
      this.historyIndex = null;
      this.onPopState = this.handlePopState.bind(this);
      window.addEventListener('popstate', this.onPopState);

      const index = this.parseHistoryHash(window.location.hash);
      if (index === null) return;

      // Landed on a step directly (reload, back from another page, shared
      // link): open there. Every step is reachable because the server
      // preselects a complete configuration; "Weiter" still validates.
      this.maxUnlockedIndex = Math.max(this.maxUnlockedIndex, index);
      this.currentIndex = index;
      this.historyIndex = index;
      this.open({ pushHistory: false });
    }

    /**
     * @param {String} hash - location.hash to read.
     * @returns {?Number} Zero-based step index, or null if the hash doesn't
     *   address a step of this configurator.
     */
    parseHistoryHash(hash) {
      const match = (hash || '').match(
        new RegExp(`^#${HISTORY_HASH_PREFIX}(\\d+)$`),
      );
      if (!match) return null;
      const index = Number(match[1]) - 1;
      if (!Number.isInteger(index) || index < 0) return null;
      return Math.min(index, this.steps.length - 1);
    }

    /**
     * @param {?Number} index - Step index, or null for the closed state.
     */
    pushHistory(index) {
      if (this.historyIndex === index) return;
      this.historyIndex = index;
      const url =
        index === null
          ? `${window.location.pathname}${window.location.search}`
          : `${window.location.pathname}${window.location.search}#${HISTORY_HASH_PREFIX}${index + 1}`;
      window.history.pushState(
        { ...(window.history.state || {}), modellConfiguratorStep: index },
        '',
        url,
      );
    }

    handlePopState() {
      const index = this.parseHistoryHash(window.location.hash);
      this.historyIndex = index;
      const isOpen = this.classList.contains('is-active');

      if (index === null) {
        if (isOpen) this.close({ pushHistory: false });
        return;
      }

      this.maxUnlockedIndex = Math.max(this.maxUnlockedIndex, index);
      this.currentIndex = index;
      if (isOpen) {
        this.scrollToSectionStart().then(() => this.renderStep());
      } else {
        this.open({ pushHistory: false });
      }
    }

    /**
     * @param {Object} [options]
     * @param {Boolean} [options.pushHistory] - Add a history entry for the
     *   opened step (false when the change CAME from history).
     */
    open({ pushHistory = true } = {}) {
      if (pushHistory) this.pushHistory(this.currentIndex);
      this.scrollToSectionStart().then(() => {
        this.classList.add('is-active');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.panel.removeAttribute('inert');
        this.panel.setAttribute('aria-hidden', 'false');
        // Re-applies the current step's state idempotently - notably the
        // "reveal the checked swatch's page" pass, in case the customer
        // paged a carousel away from their selection, closed the
        // configurator and reopened it.
        this.renderStep();
      });
    }

    /**
     * Scrolls the page back up so the section's start (stepper + step
     * heading) sits just below the sticky header. Opening the configurator
     * and switching steps usually happens via a button at the BOTTOM of the
     * current (often long, e.g. two Farbe carousels) content, so without
     * this the next step begins above the fold and the customer lands
     * mid-content. Only ever scrolls UP - if the section start is already
     * in view, it resolves immediately. Returns a Promise so callers can
     * SEQUENCE the step/panel transition after the scroll (first move up,
     * then crossfade/slide - not both at once).
     *
     * Scrolling is animated by hand (rAF + easeInOutQuad) instead of
     * window.scrollTo({behavior:'smooth'}): native smooth scrolling has no
     * completion signal to sequence the step transition on (scrollend is
     * not universally supported and any competing scroll cancels it
     * silently), and a manual tween also gives the deliberate pace the
     * step change is meant to have. A watchdog jumps instantly if the
     * first frame doesn't arrive (rAF is throttled to a standstill in
     * occluded/background windows), so the sequenced transition never
     * deadlocks behind a paused animation.
     */
    scrollToSectionStart() {
      const headerOffset =
        (typeof theme !== 'undefined' && theme.stickyHeaderHeight
          ? theme.stickyHeaderHeight()
          : 0) + 20;
      const top = Math.max(
        this.getBoundingClientRect().top + window.scrollY - headerOffset,
        0,
      );
      const start = window.scrollY;
      if (start <= top + 2) return Promise.resolve();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.scrollTo(0, top);
        return Promise.resolve();
      }

      // 350-650ms depending on distance, so short hops stay snappy and
      // long ways up don't whoosh past the whole page.
      const duration = Math.min(650, Math.max(350, (start - top) * 0.45));
      return new Promise((resolve) => {
        let settled = false;
        let t0 = null;
        const ease = (t) =>
          t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const watchdog = setTimeout(() => {
          if (settled || t0 !== null) return;
          settled = true;
          window.scrollTo(0, top);
          resolve();
        }, 250);
        const frame = (now) => {
          if (settled) return;
          if (t0 === null) t0 = now;
          const progress = Math.min((now - t0) / duration, 1);
          window.scrollTo(0, start + (top - start) * ease(progress));
          if (progress < 1) {
            requestAnimationFrame(frame);
          } else {
            settled = true;
            clearTimeout(watchdog);
            resolve();
          }
        };
        requestAnimationFrame(frame);
      });
    }

    /**
     * Restores every group inside the given step to its initial
     * (preselected) checked state — used by the Farbe step's
     * "Farbe zurücksetzen" and the Zubehör step's "Zubehör zurücksetzen"
     * link. On the Zubehör step that covers the accessory toggles
     * (included/excluded), each accessory's variant, its customer-set
     * quantity override and, with it, the card thumbnail.
     */
    resetGroups(stepId) {
      const panel = this.querySelector(
        `[data-modell-configurator-step-panel][data-step-id="${stepId}"]`,
      );
      if (!panel) return;

      panel
        .querySelectorAll('[data-modell-configurator-accessory]')
        .forEach((accessory) => {
          const toggle = accessory.querySelector(
            '[data-modell-configurator-accessory-toggle]',
          );
          if (toggle) {
            toggle.checked = toggle.defaultChecked;
            delete this.state?.qtyOverrides[toggle.dataset.entryId];
          }
        });

      panel
        .querySelectorAll('[data-modell-configurator-group]')
        .forEach((group) => {
          group
            .querySelectorAll('[data-modell-configurator-option]')
            .forEach((input) => {
              input.checked = input.defaultChecked;
            });
          this.updateSelectedClasses(group);
          this.toggleGroupError(group, false);

          group
            .querySelectorAll('[data-modell-configurator-swatches]')
            .forEach((container) => this.revealCheckedSwatchPage(container));

          // Farbe swatch carousels page via Swiper (swatch-carousel-latori,
          // no [data-modell-configurator-swatches] container) - page back to
          // the restored preselection there too.
          group
            .querySelectorAll('swatch-carousel-latori')
            .forEach((carousel) => carousel.revealCheckedSlide?.());
        });

      panel
        .querySelectorAll('[data-modell-configurator-accessory]')
        .forEach((accessory) => this.syncAccessoryThumb(accessory));

      // Restoring defaultChecked programmatically fires no change events,
      // so the pickers' "{Option}: {value}" titles and sold-out notices
      // must be refreshed here.
      panel
        .querySelectorAll('.modell-configurator__variant-select-wrapper--grow')
        .forEach((wrapper) => {
          this.updatePickerTitle(wrapper);
          this.updateSoldOutNote(wrapper);
        });
      this.updateNextButtonState(panel);

      // The preselected colours are what the model photo shows, so a reset
      // of the Farbe step puts the model photo back in the gallery.
      if (panel.querySelector('[data-variant-image-large]')) {
        this.galleryPreviewSrc = null;
        this.syncGalleryPreview();
      }

      this.renderQuantityRows();
    }

    /**
     * Größe step's "Menge zurücksetzen": drops the customer-set quantity
     * overrides for the Farbe options' rows (all sizes), so every row
     * falls back to its computed bulkQuantityBySize/globalQuantity
     * default again - the quantity analogue of the Farbe step's
     * "Farbe zurücksetzen". Accessory quantities (own overrides in the
     * same map) are deliberately left alone.
     */
    resetQuantityOverrides() {
      if (!this.state) return;
      (this.state.colors || []).forEach((color) => {
        delete this.state.qtyOverrides[color.entryId];
      });
      this.renderQuantityRows();
    }

    /**
     * @param {Object} [options]
     * @param {Boolean} [options.pushHistory] - See open().
     */
    close({ pushHistory = true } = {}) {
      if (pushHistory) this.pushHistory(null);
      this.scrollToSectionStart().then(() => {
        this.classList.remove('is-active');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.panel.setAttribute('inert', '');
        this.panel.setAttribute('aria-hidden', 'true');
        this.syncGalleryPreview();
      });
    }

    goToPrevious() {
      if (this.currentIndex === 0) {
        this.close();
        return;
      }
      this.goToStep(this.currentIndex - 1);
    }

    goToNext() {
      if (this.currentIndex >= this.steps.length - 1) return;
      if (!this.validateStep(this.steps[this.currentIndex])) return;
      this.goToStep(this.currentIndex + 1);
    }

    /**
     * @param {Number} index - Step to show.
     * @param {Object} [options]
     * @param {Boolean} [options.pushHistory] - See open().
     */
    goToStep(index, { pushHistory = true } = {}) {
      if (
        index < 0 ||
        index >= this.steps.length ||
        index === this.currentIndex
      )
        return;
      this.currentIndex = index;
      if (pushHistory) this.pushHistory(index);
      this.maxUnlockedIndex = Math.max(this.maxUnlockedIndex, index);
      // First bring the section start back into view, THEN slide the track
      // to the new step - simultaneous scrolling + sliding reads as one
      // hectic diagonal move.
      this.scrollToSectionStart().then(() => this.renderStep());
    }

    /**
     * Enforces `group.required` (not `step.required`) for every group
     * inside the given step panel. A required group is satisfied once
     * any option or size input inside it is checked - including the
     * synthetic "none of these" option when allowNegativeSelection is set.
     */
    validateStep(panel) {
      const requiredGroups = Array.from(
        panel.querySelectorAll(
          '[data-modell-configurator-group][data-required="true"]',
        ),
      );

      let isValid = true;

      requiredGroups.forEach((group) => {
        const hasSelection = !!group.querySelector(
          '[data-modell-configurator-option]:checked, [data-modell-configurator-size]:checked',
        );
        this.toggleGroupError(group, !hasSelection);
        if (!hasSelection) isValid = false;
      });

      // A selected but sold-out Farbe variant blocks the step too - its
      // per-picker notice (updateSoldOutNote) is already visible and tells
      // the customer which selection to change.
      const soldOutSelection = Array.from(
        panel.querySelectorAll('[data-modell-configurator-option]:checked'),
      ).some((input) => input.dataset.variantAvailable === 'false');
      if (soldOutSelection) isValid = false;

      return isValid;
    }

    toggleGroupError(group, hasError) {
      group.classList.toggle('is-invalid', hasError);
      const errorEl = group.querySelector(
        '[data-modell-configurator-group-error]',
      );
      if (errorEl) errorEl.hidden = !hasError;
    }

    updateSelectedClasses(group) {
      group.querySelectorAll('label').forEach((label) => {
        const input = label.querySelector('input');
        label.classList.toggle('is-selected', !!(input && input.checked));
      });
    }

    handleOptionChange(event) {
      const input = event.currentTarget;
      const group = input.closest('[data-modell-configurator-group]');
      if (!group) return;

      if (input.type === 'checkbox') {
        const groupInputs = Array.from(
          group.querySelectorAll('[data-modell-configurator-option]'),
        );
        const isNegative = input.hasAttribute('data-negative-option');

        if (isNegative && input.checked) {
          groupInputs.forEach((other) => {
            if (other !== input) other.checked = false;
          });
        } else if (!isNegative && input.checked) {
          const negativeInput = groupInputs.find((other) =>
            other.hasAttribute('data-negative-option'),
          );
          if (negativeInput) negativeInput.checked = false;
        }
      }

      this.updateSelectedClasses(group);
      this.toggleGroupError(group, false);

      if (input.checked && input.dataset.variantImageLarge) {
        this.galleryPreviewSrc = input.dataset.variantImageLarge;
        this.syncGalleryPreview();
      }

      // Before the state is re-read: readState() takes an accessory's image
      // from its card thumbnail, so the thumbnail has to show the newly
      // checked variant first.
      const accessory = input.closest('[data-modell-configurator-accessory]');
      if (accessory) this.syncAccessoryThumb(accessory);

      if (input.hasAttribute('data-bulk-quantity')) {
        this.renderQuantityRows();
      } else {
        this.updateSummary();
      }

      const pickerWrapper = input.closest(
        '.modell-configurator__variant-select-wrapper--grow',
      );
      if (pickerWrapper) {
        this.updatePickerTitle(pickerWrapper);
        this.updateSoldOutNote(pickerWrapper);
      }

      const stepPanel = input.closest('[data-modell-configurator-step-panel]');
      if (stepPanel) this.updateNextButtonState(stepPanel);
    }

    /**
     * Disables a step's "Weiter" button (attribute + the theme's
     * .btn[disabled] styling) while any selected option in that panel is a
     * sold-out variant - the button-level twin of validateStep()'s
     * sold-out check. Only the server-preselected default can be sold out
     * AND selected: sold-out swatch inputs render disabled, so the
     * customer can't select one themselves.
     */
    updateNextButtonState(panel) {
      const soldOutSelection = Array.from(
        panel.querySelectorAll('[data-modell-configurator-option]:checked'),
      ).some((input) => input.dataset.variantAvailable === 'false');

      const nextButton = panel.querySelector('[data-modell-configurator-next]');
      if (nextButton) nextButton.disabled = soldOutSelection;

      // ...and the alarm banner right above the footer explaining WHY the
      // step won't move on.
      const banner = panel.querySelector(
        '[data-modell-configurator-blocked-banner]',
      );
      if (banner) banner.hidden = !soldOutSelection;
    }

    /**
     * Shows/hides one Farbe picker's "Dieser Artikel ist leider
     * ausverkauft." notice based on whether that picker's CURRENTLY
     * checked variant is unavailable (data-variant-available, rendered
     * per swatch input in modell-configurator-variant-picker.liquid).
     * The server pre-renders the notice's initial state for the
     * preselected default; this keeps it live from then on. While any
     * such notice is visible, validateStep() refuses to advance.
     */
    updateSoldOutNote(wrapper) {
      const note = wrapper.querySelector(
        '[data-modell-configurator-sold-out-note]',
      );
      if (!note) return;
      const checked = wrapper.querySelector(
        '[data-modell-configurator-option]:checked',
      );
      note.hidden = !(checked && checked.dataset.variantAvailable === 'false');
    }

    /**
     * Fills a single image-swatch picker's own "{OptionName}: {selected
     * value}" title (.modell-configurator__picker-title) from whichever
     * swatch is currently checked INSIDE that one picker wrapper - scoped
     * to that ONE picker, never a group-level title,
     * since an accessory group can contain several independent pickers
     * (e.g. Farbe swatches + a Laenge dropdown) that each need their own
     * live value. Two swatch shapes feed this:
     * - a plain [data-modell-configurator-option] swatch (the flat
     *   single-dimension image widget) - label + value come from its own
     *   data-option-name/data-variant-title.
     * - a [data-modell-configurator-variant-swatch] (an image dimension
     *   nested inside a multi-dimension select-branch picker) - only
     *   carries the raw value (data-value); label stays whatever Liquid
     *   already rendered for that dimension's name.
     */
    updatePickerTitle(wrapper) {
      const valueEl = wrapper.querySelector(
        '[data-modell-configurator-picker-title-value]',
      );
      if (!valueEl) return;

      const labelEl = wrapper.querySelector(
        '[data-modell-configurator-picker-title-label]',
      );

      const optionInput = wrapper.querySelector(
        '[data-modell-configurator-option]:checked',
      );
      if (optionInput) {
        if (labelEl) {
          labelEl.textContent =
            optionInput.dataset.optionName || labelEl.textContent;
        }
        valueEl.textContent = optionInput.dataset.variantTitle || '';
        const artnrEl = wrapper.querySelector(
          '[data-modell-configurator-picker-title-artnr]',
        );
        if (artnrEl) artnrEl.textContent = optionInput.dataset.variantSku || '';
        return;
      }

      const dimensionSwatch = wrapper.querySelector(
        '[data-modell-configurator-variant-swatch]:checked',
      );
      if (dimensionSwatch) {
        valueEl.textContent = dimensionSwatch.dataset.value || '';
      }
    }

    handleSizeChange(event) {
      const input = event.currentTarget;
      const group = input.closest('[data-modell-configurator-group]');
      if (group) {
        this.updateSelectedClasses(group);
        this.toggleGroupError(group, false);
      }
      this.renderQuantityRows();
    }

    /**
     * Points a Zubehör accessory card's thumbnail at its currently checked
     * variant's image (`data-variant-image`), so the card shows the chosen
     * button/colour rather than the product's featured image - and so does
     * the "Mein Modell" summary and the quantity rows, which read the
     * accessory's image from this very <img> in readState(). A variant
     * without its own image leaves the current thumbnail (the product
     * image rendered by Liquid) untouched.
     */
    syncAccessoryThumb(accessory) {
      const img = accessory.querySelector(
        '[data-modell-configurator-accessory-thumb] img',
      );
      const checked = accessory.querySelector(
        '[data-modell-configurator-option]:checked',
      );
      if (!img || !checked) return;
      const src = checked.dataset.variantImage;
      if (!src || img.getAttribute('src') === src) return;
      img.setAttribute('src', src);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    }

    /**
     * Handles turning a Zubehör accessory card on/off. The sub-variant
     * picker and "Menge" stepper are ALWAYS visible and keep their own
     * always-has-a-default checked variant regardless of this toggle
     * (same "always has a selection" pattern as Farbe) - each accessory
     * already ships with a configured default (preselectedVariantId /
     * bulkQuantityBySize), so it starts checked/included, and the picker
     * just lets the customer swap to a different variant if they want.
     * The toggle itself is the SOLE signal for whether it's actually
     * counted (see `updateSummary()`, which additionally requires the
     * accessory's own toggle to be checked for accessory-owned options,
     * on top of the picker's own checked variant) - unchecking it is how
     * the customer opts OUT of an otherwise-included accessory.
     */
    handleAccessoryToggleChange(event) {
      const toggle = event.currentTarget;
      const group = toggle.closest('[data-modell-configurator-group]');
      if (group) {
        this.updateSelectedClasses(group);
        this.toggleGroupError(group, false);
      }

      this.renderQuantityRows();
      this.updateSummary();
    }

    /**
     * Splits a `[data-modell-configurator-swatches]` container's real
     * variant swatches (`.modell-configurator__swatch` labels - dropdown
     * widgets have none of these, so this is a no-op for them) into
     * fixed-size pages (`data-page-size`, default 16) once there are more
     * than one page's worth, building numbered page buttons + a "next"
     * button into the sibling `[data-modell-configurator-pagination]`
     * container (Farbe yarns/Zubehör accessories can have dozens of real
     * color variants - e.g. a full "Capri" patchwork blanket yarn - which
     * would otherwise render as one endless unpaginated grid). Starts on
     * whichever page contains the currently checked swatch (the
     * preselected default), not always page 1, so the customer's current
     * selection is never hidden on load.
     */
    initSwatchPagination(container) {
      // Defense-in-depth: exactly one [data-modell-configurator-swatches]
      // container must own a given swatch set - containers must never
      // nest. If this one itself contains another (shouldn't happen after
      // the Farbe step's de-nesting fix, but guards against regressions),
      // skip it: initialising pagination on both an outer and inner
      // container over the SAME swatch nodes causes last-writer-wins
      // `dataset.page` assignment (an outer nav's page can end up
      // matching zero swatches, or bars can duplicate).
      if (container.querySelector('[data-modell-configurator-swatches]')) {
        return;
      }

      const nav = container.nextElementSibling;
      if (!nav || !nav.matches('[data-modell-configurator-pagination]'))
        return;

      if (container.hasAttribute('data-page-size')) {
        this.initFixedSwatchPagination(container, nav);
        return;
      }

      this.initRowBasedSwatchPagination(container, nav);
    }

    /**
     * Legacy fixed-item-count-per-page mode - only used by containers
     * that explicitly carry a `data-page-size` attribute (currently just
     * the top-level Farbe step's swatches, modell-configurator-step-
     * farbe.liquid - left untouched on purpose). Behavior identical to
     * before this method was split.
     */
    initFixedSwatchPagination(container, nav) {
      const swatches = Array.from(
        container.querySelectorAll('.modell-configurator__swatch'),
      );
      if (!swatches.length) return;

      const pageSize = Number(container.dataset.pageSize) || 16;
      const pageCount = Math.ceil(swatches.length / pageSize);

      swatches.forEach((swatch, index) => {
        swatch.dataset.page = Math.floor(index / pageSize);
      });

      if (pageCount <= 1) {
        nav.hidden = true;
        return;
      }

      this.buildPaginationNav(container, nav, pageCount);
      this.revealCheckedSwatchPage(container);
    }

    /**
     * Dynamic row-count-based mode - used whenever a swatches container
     * has NO `data-page-size` (the Zubehör accessory pickers: both the
     * pure-image widget and the Farbe-in-select-group widget). A page is
     * a fixed number of visually-wrapped ROWS (`rowsPerPage`), never a
     * fixed number of items - how many swatches actually fit per row is
     * discovered by reading each swatch's own rendered `offsetTop` after
     * the container's `flex-wrap: wrap` layout has settled, so it's
     * always a function of the container's real width (which itself is
     * now decoupled from the accessory card/title's width - see
     * modell-configurator-step-options.liquid's accessory-row docs), not
     * a hard-coded count. Recomputed via ResizeObserver so narrowing the
     * viewport (or the options panel) reflows the page split live.
     */
    initRowBasedSwatchPagination(container, nav) {
      const rowsPerPage = 2;

      const rebuild = () => {
        const swatches = Array.from(
          container.querySelectorAll('.modell-configurator__swatch'),
        );
        if (!swatches.length) return;

        // The container is as wide as its content. A short page (for
        // example 4 swatches) makes it narrower, and the next page then
        // wraps into more rows than measured (client report 2026-10-06:
        // 3 rows instead of 2, with an empty place). Thus measure with all
        // swatches shown at the natural width, then keep that width.
        container.style.minWidth = '';
        swatches.forEach((swatch) =>
          swatch.classList.remove('is-page-hidden'),
        );
        const fullWidth = container.getBoundingClientRect().width;
        if (fullWidth > 0) container.style.minWidth = `${fullWidth}px`;

        const rowOffsets = [];
        swatches.forEach((swatch) => {
          const top = swatch.offsetTop;
          if (!rowOffsets.some((existing) => Math.abs(existing - top) < 1)) {
            rowOffsets.push(top);
          }
        });
        rowOffsets.sort((a, b) => a - b);

        swatches.forEach((swatch) => {
          const rowIndex = rowOffsets.findIndex(
            (top) => Math.abs(top - swatch.offsetTop) < 1,
          );
          swatch.dataset.page = Math.floor(rowIndex / rowsPerPage);
        });

        const pageCount = Math.max(
          1,
          Math.ceil(rowOffsets.length / rowsPerPage),
        );

        if (pageCount <= 1) {
          nav.hidden = true;
          nav.textContent = '';
          this.showSwatchPage(container, 0);
          return;
        }

        this.buildPaginationNav(container, nav, pageCount);
        this.revealCheckedSwatchPage(container);
      };

      rebuild();

      // Do NOT observe `container`: rebuild() shows/hides swatches, changing
      // the container's own box (width AND height - its grid column is
      // content-sized), which a self-observer reports as a resize -> re-runs
      // rebuild -> infinite loop (measured: 219 "ResizeObserver loop"
      // warnings + ~1250 class toggles/sec, viewport-independent). A window
      // resize cannot self-trigger from DOM writes, so debounce off that
      // instead. Tradeoff: this re-splits pages on window resize only, not
      // on panel-width changes without one (e.g. toggling a sibling
      // accessory) - call rebuild() directly from those handlers if that
      // gap ever shows up in practice.
      let resizeRaf = 0;
      const onViewportResize = () => {
        cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(rebuild);
      };
      window.addEventListener('resize', onViewportResize);

      // The window resize is not sufficient. The split was made at the width
      // of the first layout, but the container width changes also when the
      // step panel opens or when the accessory row changes its layout. Then
      // a page made for 7 swatches in a row showed 3 rows of 5 with an empty
      // place (client report 2026-10-06). Thus also observe the width of the
      // step panel. Its width does not depend on the swatches, so this
      // cannot cause the loop described above. Ignore a width of 0 (the
      // panel is hidden) and repeated reports of the same width.
      const panel = container.closest('[data-modell-configurator-step-panel]');
      if (panel && 'ResizeObserver' in window) {
        let lastWidth = panel.getBoundingClientRect().width;
        new ResizeObserver((entries) => {
          const width = entries[0].contentRect.width;
          if (width === 0 || Math.abs(width - lastWidth) < 1) return;
          lastWidth = width;
          onViewportResize();
        }).observe(panel);
      }
    }

    /**
     * Builds the numbered page buttons + "next" button for a swatches
     * container's pagination nav - shared by both the fixed-page-size
     * and row-based pagination modes.
     */
    buildPaginationNav(container, nav, pageCount) {
      nav.textContent = '';
      nav.hidden = false;

      for (let page = 0; page < pageCount; page += 1) {
        const pageButton = document.createElement('button');
        pageButton.type = 'button';
        pageButton.className = 'modell-configurator__pagination-page';
        pageButton.textContent = String(page + 1);
        pageButton.setAttribute('data-modell-configurator-pagination-page', '');
        pageButton.dataset.page = page;
        pageButton.addEventListener('click', () => {
          this.showSwatchPage(container, page);
        });
        nav.appendChild(pageButton);
      }

      const nextButton = document.createElement('button');
      nextButton.type = 'button';
      nextButton.className = 'modell-configurator__pagination-next';
      nextButton.setAttribute('data-modell-configurator-pagination-next', '');
      nextButton.setAttribute(
        'aria-label',
        nav.dataset.paginationNextLabel || 'Next',
      );
      nextButton.textContent = '\u203a';
      nextButton.addEventListener('click', () => {
        const current = Number(container.dataset.currentPage) || 0;
        this.showSwatchPage(
          container,
          Math.min(current + 1, pageCount - 1),
        );
      });
      nav.appendChild(nextButton);
    }

    /**
     * Shows the page containing whichever swatch is currently checked
     * inside a paginated container - falls back to page 0 when nothing is
     * checked yet. Used on init, after the Farbe "reset selection" link,
     * and after a Zubehör accessory is toggled on (its default variant
     * might not live on page 1).
     */
    revealCheckedSwatchPage(container) {
      const checked = container.querySelector(
        '.modell-configurator__swatch input:checked',
      );
      const page = checked
        ? Number(checked.closest('.modell-configurator__swatch').dataset.page)
        : 0;
      this.showSwatchPage(container, page || 0);
    }

    showSwatchPage(container, page) {
      const nav = container.nextElementSibling;
      if (!nav) return;

      container
        .querySelectorAll('.modell-configurator__swatch')
        .forEach((swatch) => {
          swatch.classList.toggle(
            'is-page-hidden',
            Number(swatch.dataset.page) !== page,
          );
        });

      container.dataset.currentPage = page;

      const pageButtons = Array.from(
        nav.querySelectorAll('[data-modell-configurator-pagination-page]'),
      );
      pageButtons.forEach((button) => {
        button.classList.toggle(
          'is-active',
          Number(button.dataset.page) === page,
        );
      });

      const nextButton = nav.querySelector(
        '[data-modell-configurator-pagination-next]',
      );
      if (nextButton) {
        nextButton.disabled = page >= pageButtons.length - 1;
      }
    }

    /**
     * Handles a change on one of a variant dimension's pickers - either a
     * plain <select> dropdown (Länge/Stärke/Größe/...) or an inline image
     * swatch group (a "Farbe" dimension, which still renders as swatches
     * even inside an otherwise dropdown-driven "select" widget). Collects
     * the current value of EVERY dimension sharing the same
     * `data-product-id` (dropdowns by their own `.value`, swatch groups by
     * whichever radio is `:checked`), ordered by `data-option-index`,
     * finds the hidden option input whose `data-variant-options` array
     * matches that full combination, and checks it - dispatching a real
     * 'change' event so the existing handleOptionChange/quantity/summary
     * logic runs exactly as it does for plain swatches and sizes.
     */
    handleVariantOptionChange(event) {
      const control = event.currentTarget;
      const wrapper = control.closest(
        '[data-modell-configurator-variant-selects]',
      );
      if (!wrapper) return;

      const valuesByIndex = new Map();

      wrapper
        .querySelectorAll('[data-modell-configurator-variant-select]')
        .forEach((select) => {
          valuesByIndex.set(Number(select.dataset.optionIndex), select.value);
        });

      wrapper
        .querySelectorAll('[data-modell-configurator-variant-swatch]:checked')
        .forEach((swatch) => {
          valuesByIndex.set(
            Number(swatch.dataset.optionIndex),
            swatch.dataset.value,
          );
        });

      const values = Array.from(valuesByIndex.keys())
        .sort((a, b) => a - b)
        .map((index) => valuesByIndex.get(index));

      const productId = wrapper.dataset.productId;
      const group = control.closest('[data-modell-configurator-group]');
      if (!group) return;

      const candidates = group.querySelectorAll(
        `[data-modell-configurator-option][data-product-id="${productId}"]`,
      );

      for (const input of candidates) {
        let variantOptions = [];
        try {
          variantOptions = JSON.parse(input.dataset.variantOptions || '[]');
        } catch (error) {
          variantOptions = [];
        }

        const matches =
          variantOptions.length === values.length &&
          variantOptions.every((value, index) => value === values[index]);

        if (matches) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }

      const swatchContainer = control.closest(
        '[data-modell-configurator-swatches]',
      );
      if (swatchContainer) this.revealCheckedSwatchPage(swatchContainer);

      const pickerWrapper = control.closest(
        '.modell-configurator__variant-select-wrapper--grow',
      );
      if (pickerWrapper) this.updatePickerTitle(pickerWrapper);
    }

    /**
     * Rebuilds every quantity-list container's rows - one row per
     * currently checked option that carries `data-bulk-quantity` (only
     * consumable/`userDefined` options do, e.g. Farbe yarns or Zubehör
     * accessories), scoped to whichever step panel each container's
     * `data-source-taxonomy` points at (the Größe step's list points at
     * "farbe"; the options step's list points at its own taxonomy since
     * the size is already known by the time Anleitung is selected).
     * Zubehör accessories are EXCLUDED here (`.closest('[data-modell-
     * configurator-accessory]')` filter) since each one now gets its own
     * inline quantity stepper via `renderAccessoryQuantity()` (called
     * below for every `this.accessories` entry) instead of a shared
     * bottom list row - otherwise the same accessory would show a
     * quantity control twice. Each row gets a +/- stepper defaulting to
     * that option's bulk quantity for the selected size - falling back to
     * the FIRST available size (not a flat "1") when no size is checked
     * yet, since the Größe step's first size is itself checked by default
     * (same "always has a selection" pattern as Farbe), so this only
     * matters as a defensive fallback if every size were unavailable.
     * Built with DOM APIs (not innerHTML) since option labels come from
     * merchant-entered product titles.
     */
    renderQuantityRows() {
      this.readStateFromDom();
      const checkedSize = this.querySelector(
        '[data-modell-configurator-size]:checked',
      );
      const firstSize = this.querySelector('[data-modell-configurator-size]');
      const size = checkedSize
        ? checkedSize.dataset.size
        : firstSize
          ? firstSize.dataset.size
          : null;

      this.quantityLists.forEach((list) => {
        const sourceTaxonomy = list.dataset.sourceTaxonomy;
        const sourcePanel = sourceTaxonomy
          ? this.querySelector(
              `[data-modell-configurator-step-panel][data-step-taxonomy="${sourceTaxonomy}"]`,
            )
          : null;
        if (!sourcePanel) {
          list.textContent = '';
          list.hidden = true;
          return;
        }

        const unitLabel = list.dataset.quantityUnit || '';
        const decreaseLabel = list.dataset.quantityDecreaseLabel || '';
        const increaseLabel = list.dataset.quantityIncreaseLabel || '';
        const mengeLabel = list.dataset.quantityMengeLabel || '';
        const artnrLabel = list.dataset.quantityArtnrLabel || '';

        if (mengeLabel) {
          const colors = this.state.colors;
          list.textContent = '';
          list.hidden = colors.length === 0;
          colors.forEach((color) => {
            list.appendChild(
              this.buildQuantityRow({
                productTitle: color.productTitle,
                variantTitle: color.variantTitle,
                artNr: color.artNr,
                image: color.image,
                artnrLabel: artnrLabel,
                mengeLabel: mengeLabel,
                showName: true,
                quantity: this.resolveColorQty(color, size),
                unitLabel: unitLabel,
                decreaseLabel: decreaseLabel,
                increaseLabel: increaseLabel,
                entryId: color.entryId,
                variantId: color.variantId || '',
              }),
            );
          });
          return;
        }

        const colorInputs = Array.from(
          sourcePanel.querySelectorAll(
            '[data-modell-configurator-option][data-bulk-quantity]:checked',
          ),
        ).filter(
          (input) => !input.closest('[data-modell-configurator-accessory]'),
        );

        list.textContent = '';
        list.hidden = colorInputs.length === 0;

        colorInputs.forEach((input) => {
          const wrapperLabel = input.closest('label');
          const labelEl = wrapperLabel
            ? wrapperLabel.querySelector(
                '.modell-configurator__swatch-label, .modell-configurator__option-label',
              )
            : null;
          const title = labelEl
            ? labelEl.textContent.trim()
            : input.dataset.variantTitle || '';

          const row = this.buildQuantityRow({
            title,
            showName: true,
            quantity: this.resolveQty(input, size),
            unitLabel,
            decreaseLabel,
            increaseLabel,
            entryId: input.dataset.entryId,
            variantId: input.dataset.variantId || '',
          });

          list.appendChild(row);
        });
      });

      this.accessories.forEach((accessory) =>
        this.renderAccessoryQuantity(accessory, size),
      );

      this.updateSummary();
    }

    /**
     * Phase 0 state scaffold. The single object that holds the
     * configurator's current selection; consumers migrate onto it step by
     * step. For now `qtyOverrides` is the live source of truth for
     * per-size quantities (see resolveQty), while `size`/`colors` mirror
     * the DOM for the later title/Groesse phases.
     */
    initState() {
      this.state = {
        size: null,
        colors: [],
        accessories: [],
        instruction: null,
        qtyOverrides: {},
      };
    }

    /** Currently checked Groesse (falls back to the first size tile). */
    currentSize() {
      const checked = this.querySelector(
        '[data-modell-configurator-size]:checked',
      );
      const first = this.querySelector('[data-modell-configurator-size]');
      return checked
        ? checked.dataset.size
        : first
          ? first.dataset.size
          : null;
    }

    /**
     * Mirrors the current DOM selection into `this.state` (size + Farbe
     * colors). Never touches `qtyOverrides`, which is owned by customer
     * input only. Called at the top of renderQuantityRows so the state
     * stays fresh on every structural change.
     */
    readStateFromDom() {
      if (!this.state) this.initState();
      this.state.size = this.currentSize();

      const farbePanel = this.querySelector(
        '[data-modell-configurator-step-panel][data-step-taxonomy="farbe"]',
      );
      const colorInputs = farbePanel
        ? Array.from(
            farbePanel.querySelectorAll(
              '[data-modell-configurator-option][data-bulk-quantity]:checked',
            ),
          ).filter(
            (input) => !input.closest('[data-modell-configurator-accessory]'),
          )
        : [];

      this.state.colors = colorInputs.map((input) => {
        const group = input.closest('[data-modell-configurator-group]');
        let bulkQuantity = {};
        try {
          bulkQuantity = JSON.parse(input.dataset.bulkQuantity || '{}') || {};
        } catch (error) {
          bulkQuantity = {};
        }
        return {
          entryId: input.dataset.entryId,
          groupId: group ? group.dataset.groupId : '',
          productId: input.dataset.productId,
          variantId: input.dataset.variantId,
          productTitle: input.dataset.productTitle || '',
          variantTitle: input.dataset.variantTitle || '',
          artNr: input.dataset.variantSku || '',
          optionName: input.dataset.optionName || '',
          image: input.dataset.variantImage || '',
          price: input.dataset.variantPrice
            ? Number(input.dataset.variantPrice)
            : null,
          bulkQuantity: bulkQuantity,
          globalQuantity: input.dataset.globalQuantity || '',
        };
      });

      // Anleitung: the single checked (non-negative) option, if any.
      const anleitungPanel = this.querySelector(
        '[data-modell-configurator-step-panel][data-step-taxonomy="anleitung"]',
      );
      const instrInput = anleitungPanel
        ? anleitungPanel.querySelector(
            '[data-modell-configurator-option]:checked:not([data-negative-option])',
          )
        : null;
      if (instrInput) {
        const instrCard =
          instrInput.closest('.modell-configurator__card') ||
          instrInput.closest('label');
        const instrImg = instrCard ? instrCard.querySelector('img') : null;
        this.state.instruction = {
          entryId: instrInput.dataset.entryId,
          title:
            instrInput.dataset.productTitle ||
            instrInput.dataset.variantTitle ||
            '',
          image: instrImg ? instrImg.getAttribute('src') : '',
          variantId: instrInput.dataset.variantId || '',
          price: instrInput.dataset.variantPrice
            ? Number(instrInput.dataset.variantPrice)
            : null,
        };
      } else {
        this.state.instruction = null;
      }

      // Zubehör: one entry per accessory, with its selected variant's meta.
      this.state.accessories = this.accessories.map((acc) => {
        const toggle = acc.querySelector(
          '[data-modell-configurator-accessory-toggle]',
        );
        const opt = acc.querySelector(
          '[data-modell-configurator-option]:checked',
        );
        const accImg = acc.querySelector(
          '.modell-configurator__card-media img',
        );
        return {
          entryId: toggle
            ? toggle.dataset.entryId
            : opt
              ? opt.dataset.entryId
              : '',
          included: toggle ? !!toggle.checked : false,
          title: opt ? opt.dataset.productTitle || '' : '',
          image: accImg ? accImg.getAttribute('src') : '',
          variantId: opt ? opt.dataset.variantId || '' : '',
          metaParts: this.metaPartsFromOption(opt),
          qty: opt ? this.resolveQty(opt, this.state.size) : 1,
          price:
            opt && opt.dataset.variantPrice
              ? Number(opt.dataset.variantPrice)
              : null,
        };
      });
    }

    /**
     * Zips an option input's dimension names (`data-option-name`, e.g.
     * "Nadelstärke / Nadellänge") with its selected values (JSON
     * `data-variant-options` when present, else the ' / '-joined
     * `data-variant-title`) into [{label, value}] meta parts. Both
     * attributes are already on every option input - no Liquid change.
     */
    metaPartsFromOption(opt) {
      if (!opt) return [];
      const names = (opt.dataset.optionName || '').split(' / ');
      let values = null;
      try {
        const parsed = JSON.parse(opt.dataset.variantOptions || 'null');
        if (Array.isArray(parsed)) values = parsed;
      } catch (error) {
        values = null;
      }
      if (!values) values = (opt.dataset.variantTitle || '').split(' / ');
      return names
        .map((label, index) => ({
          label: (label || '').trim(),
          value: (values[index] == null ? '' : String(values[index])).trim(),
        }))
        .filter((part) => part.label && part.value);
    }

    /**
     * Quantity to prefill for a `data-bulk-quantity` option, override
     * first: a value the customer set for THIS option at THIS size wins
     * (remembered per size), otherwise the size/global default from
     * resolveBulkQuantity. `entryId + size` is the memory key; a null size
     * (no Groesse step) collapses into a single `__nosize__` bucket.
     */
    resolveQty(input, size) {
      const entryId = input.dataset.entryId;
      const sizeKey = size || '__nosize__';
      const override =
        this.state &&
        this.state.qtyOverrides[entryId] &&
        this.state.qtyOverrides[entryId][sizeKey];
      if (Number.isFinite(override)) return override;
      return this.resolveBulkQuantity(input, size);
    }

    /** Remembers a customer-set quantity for one option at one size. */
    setQtyOverride(entryId, size, qty) {
      if (!entryId || !Number.isFinite(qty)) return;
      if (!this.state) this.initState();
      const sizeKey = size || '__nosize__';
      if (!this.state.qtyOverrides[entryId]) {
        this.state.qtyOverrides[entryId] = {};
      }
      this.state.qtyOverrides[entryId][sizeKey] = qty;
    }

    /**
     * Resolves the quantity to prefill for a `data-bulk-quantity` option -
     * that size's entry in `bulkQuantityBySize` if present, else the
     * option's flat `data-global-quantity`, else 1.
     */
    resolveBulkQuantity(input, size) {
      let bulkQuantity = {};
      try {
        bulkQuantity = JSON.parse(input.dataset.bulkQuantity || '{}') || {};
      } catch (error) {
        bulkQuantity = {};
      }

      let quantity = Number(input.dataset.globalQuantity) || 1;
      if (size && Object.prototype.hasOwnProperty.call(bulkQuantity, size)) {
        const sizeQuantity = Number(bulkQuantity[size]);
        if (Number.isFinite(sizeQuantity)) quantity = sizeQuantity;
      }
      return quantity;
    }

    /**
     * Like resolveQty but reading from a `state.colors` entry (the Groesse
     * step renders from state, not from the Farbe DOM): the customer's
     * per-size override wins, else this size's bulk default, else global.
     */
    resolveColorQty(color, size) {
      const sizeKey = size || '__nosize__';
      const override =
        this.state &&
        this.state.qtyOverrides[color.entryId] &&
        this.state.qtyOverrides[color.entryId][sizeKey];
      if (Number.isFinite(override)) return override;

      let quantity = Number(color.globalQuantity) || 1;
      const bulk = color.bulkQuantity || {};
      if (size && Object.prototype.hasOwnProperty.call(bulk, size)) {
        const sizeQuantity = Number(bulk[size]);
        if (Number.isFinite(sizeQuantity)) quantity = sizeQuantity;
      }
      return quantity;
    }

    /** One "| text" meta segment for a quantity row's identity line. */
    buildQuantityMeta(text) {
      const meta = document.createElement('span');
      meta.className = 'modell-configurator__group-title-meta';
      const sep = document.createElement('span');
      sep.className = 'modell-configurator__group-title-sep';
      sep.textContent = '|';
      meta.appendChild(sep);
      meta.appendChild(document.createTextNode(' ' + text));
      return meta;
    }

    /**
     * Builds one quantity-row element (name + -/input/+ stepper + unit) -
     * shared by the multi-row `data-modell-configurator-quantity-list`
     * containers and each Zubehör accessory's own single inline slot.
     * `showName` is false for the inline accessory slot since its title is
     * already shown right next to it in the same row. `showUnit` (default
     * true) is false for that same inline slot too, to keep the one-line
     * accessory row compact - the shared multi-row lists still show it.
     */
    buildQuantityRow({
      title,
      image,
      productTitle,
      variantTitle,
      artNr,
      artnrLabel,
      mengeLabel,
      showName,
      showUnit = true,
      quantity,
      unitLabel,
      decreaseLabel,
      increaseLabel,
      entryId,
      variantId,
    }) {
      const row = document.createElement('div');
      row.className = 'modell-configurator__quantity-row';
      row.setAttribute('data-modell-configurator-quantity-row', '');
      row.setAttribute('data-entry-id', entryId || '');
      row.setAttribute('data-variant-id', variantId || '');

      if (image) {
        const media = document.createElement('span');
        media.className = 'modell-configurator__quantity-media';
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
        row.appendChild(media);
      }

      if (showName) {
        const info = document.createElement('span');
        info.className = 'modell-configurator__quantity-info';
        const nameEl = document.createElement('span');
        nameEl.className = 'modell-configurator__quantity-name';
        nameEl.textContent = productTitle || title;
        info.appendChild(nameEl);
        if (productTitle) {
          if (variantTitle) {
            info.appendChild(this.buildQuantityMeta(variantTitle));
          }
          if (artNr) {
            const artText = (artnrLabel ? artnrLabel + ': ' : '') + artNr;
            info.appendChild(this.buildQuantityMeta(artText));
          }
        }
        row.appendChild(info);
      }

      if (mengeLabel) {
        const mengeEl = document.createElement('span');
        mengeEl.className = 'modell-configurator__quantity-menge-label';
        mengeEl.textContent = mengeLabel;
        row.appendChild(mengeEl);
      }

      // Same UI as the PDP's quantity selector (main-product.liquid's
      // <quantity-wrapper class="buttoned-input">): a bordered pill with
      // icon-minus/icon-plus anchors around a bare number input, styled
      // entirely by main.css's .buttoned-input rules. Only the data
      // attributes (this component's own +/- contract, handled in
      // connectedCallback's click listener with preventDefault) differ.
      const controls = document.createElement('div');
      controls.className = 'buttoned-input modell-configurator__quantity-control';

      const minusSvg =
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" class="icon" aria-hidden="true" focusable="false" role="presentation"><path d="M5 12h14"/></svg>';
      const plusSvg =
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" class="icon" aria-hidden="true" focusable="false" role="presentation"><path d="M12 5v14M5 12h14"/></svg>';

      const decrementButton = document.createElement('a');
      decrementButton.href = '#';
      decrementButton.innerHTML = minusSvg;
      decrementButton.setAttribute(
        'data-modell-configurator-quantity-decrement',
        '',
      );
      decrementButton.setAttribute('aria-label', decreaseLabel);

      const quantityInput = document.createElement('input');
      quantityInput.type = 'number';
      quantityInput.min = '1';
      quantityInput.className = 'modell-configurator__quantity-input';
      quantityInput.setAttribute('data-modell-configurator-quantity', '');
      quantityInput.setAttribute('aria-label', mengeLabel || unitLabel || '');
      quantityInput.value = quantity;

      const incrementButton = document.createElement('a');
      incrementButton.href = '#';
      incrementButton.innerHTML = plusSvg;
      incrementButton.setAttribute(
        'data-modell-configurator-quantity-increment',
        '',
      );
      incrementButton.setAttribute('aria-label', increaseLabel);

      controls.appendChild(decrementButton);
      controls.appendChild(quantityInput);
      controls.appendChild(incrementButton);
      row.appendChild(controls);

      if (showUnit) {
        const unitEl = document.createElement('span');
        unitEl.className = 'modell-configurator__quantity-unit';
        unitEl.textContent = unitLabel;
        row.appendChild(unitEl);
      }

      return row;
    }

    /**
     * Fills (or clears) one Zubehör accessory's own inline quantity slot -
     * only shown while that accessory is toggled on AND its sub-variant
     * picker resolves to a real `data-bulk-quantity` variant. Toggles the
     * WHOLE `data-modell-configurator-quantity-wrapper` (not just the
     * slot) so its "Menge" label hides/shows together with the stepper.
     * Reuses the quantity-list containers' unit/+-/- labels (falls back
     * to `list.dataset` since the slot itself doesn't carry them) and
     * always rebuilds from scratch, matching the shared list's existing
     * behavior (a manually adjusted quantity is discarded if anything
     * re-triggers this, e.g. switching size or the accessory's own
     * variant - a pre-existing quirk, not new).
     */
    renderAccessoryQuantity(accessory, size) {
      const wrapper = accessory.querySelector(
        '[data-modell-configurator-quantity-wrapper]',
      );
      const slot = accessory.querySelector(
        '[data-modell-configurator-quantity-slot]',
      );
      if (!wrapper || !slot) return;

      const input = accessory.querySelector(
        '[data-modell-configurator-option][data-bulk-quantity]:checked',
      );

      slot.textContent = '';
      if (!input) return;

      const referenceList = this.quantityLists[0];
      const unitLabel = referenceList?.dataset.quantityUnit || '';
      const decreaseLabel = referenceList?.dataset.quantityDecreaseLabel || '';
      const increaseLabel = referenceList?.dataset.quantityIncreaseLabel || '';

      const resolvedSize =
        size !== undefined
          ? size
          : (() => {
              const checkedSize = this.querySelector(
                '[data-modell-configurator-size]:checked',
              );
              const firstSize = this.querySelector(
                '[data-modell-configurator-size]',
              );
              return checkedSize
                ? checkedSize.dataset.size
                : firstSize
                  ? firstSize.dataset.size
                  : null;
            })();

      const row = this.buildQuantityRow({
        title: input.dataset.variantTitle || '',
        showName: false,
        showUnit: false,
        quantity: this.resolveQty(input, resolvedSize),
        unitLabel,
        decreaseLabel,
        increaseLabel,
        entryId: input.dataset.entryId,
        variantId: input.dataset.variantId || '',
      });

      slot.appendChild(row);
    }

    /**
     * Rebuilds the summary step's read-back list from current selections.
     * Phase 1 only: no live price yet, just titles/size/quantity. Built
     * with DOM APIs (not innerHTML) since option labels come from
     * merchant-entered product titles.
     */
    updateSummary() {
      if (!this.summaryContainer) return;
      this.readStateFromDom();

      const container = this.summaryContainer;
      container.textContent = '';

      const artnrLabel = container.dataset.artnrLabel || '';
      const mengeLabel = container.dataset.mengeLabel || '';
      const unitLabel = container.dataset.unitLabel || '';
      const changeLabel = container.dataset.changeLabel || '';

      let total = 0;
      let hasPriced = false;

      const panels = Array.from(
        this.querySelectorAll('[data-modell-configurator-step-panel]'),
      ).filter((panel) => panel.dataset.stepTaxonomy);

      panels.forEach((panel) => {
        const taxonomy = panel.dataset.stepTaxonomy;
        const label = panel.dataset.stepLabel || taxonomy;
        const index = Number(panel.dataset.stepIndex);
        const lines = [];

        const priceLine = (qty, price) => {
          if (!Number.isFinite(price)) return '';
          total += price * qty;
          hasPriced = true;
          return qty + ' × ' + this.formatMoney(price);
        };

        if (taxonomy === 'farbe') {
          this.state.colors.forEach((color) => {
            const qty = this.resolveColorQty(color, this.state.size);
            const parts = [];
            if (color.optionName && color.variantTitle) {
              parts.push(color.optionName + ': ' + color.variantTitle);
            }
            if (color.artNr) parts.push(artnrLabel + ': ' + color.artNr);
            const priceText = priceLine(qty, color.price);
            if (!priceText) {
              parts.push(
                mengeLabel + ': ' + qty + (unitLabel ? ' ' + unitLabel : ''),
              );
            }
            lines.push({
              image: color.image,
              title: color.productTitle,
              parts,
              priceText,
            });
          });
        } else if (taxonomy === 'groesse') {
          if (this.state.size) {
            lines.push({ title: this.state.size, parts: [], priceText: '' });
          }
        } else if (taxonomy === 'anleitung') {
          if (this.state.instruction) {
            const priceText = priceLine(1, this.state.instruction.price);
            lines.push({
              image: this.state.instruction.image,
              title: this.state.instruction.title,
              parts: [],
              priceText,
            });
          }
        } else if (taxonomy === 'zubehoer') {
          this.state.accessories
            .filter((accessory) => accessory.included)
            .forEach((accessory) => {
              const parts = accessory.metaParts.map(
                (meta) => meta.label + ': ' + meta.value,
              );
              const priceText = priceLine(accessory.qty, accessory.price);
              if (!priceText) {
                parts.push(mengeLabel + ': ' + accessory.qty);
              }
              lines.push({
                image: accessory.image,
                title: accessory.title,
                parts,
                priceText,
              });
            });
        }

        if (!lines.length) return;

        const section = document.createElement('div');
        section.className = 'modell-configurator__summary-section';

        const head = document.createElement('div');
        head.className = 'modell-configurator__summary-section-head';
        const heading = document.createElement('h4');
        heading.className = 'modell-configurator__summary-section-title';
        // No step numbering here - the numbers live in the stepper bar
        // above, exactly as in the step panels' own headings.
        heading.textContent = label;
        head.appendChild(heading);
        if (changeLabel && Number.isFinite(index)) {
          const change = document.createElement('button');
          change.type = 'button';
          change.className =
            'modell-configurator__reset-link modell-configurator__summary-change';
          change.textContent = changeLabel;
          change.addEventListener('click', () => this.goToStep(index));
          head.appendChild(change);
        }
        section.appendChild(head);

        lines.forEach((line) => {
          const row = document.createElement('div');
          row.className = 'modell-configurator__summary-line';
          if (line.image) {
            const media = document.createElement('span');
            media.className = 'modell-configurator__summary-media';
            const img = document.createElement('img');
            img.src = line.image;
            img.alt = '';
            img.loading = 'lazy';
            media.appendChild(img);
            row.appendChild(media);
          }
          const body = document.createElement('span');
          body.className = 'modell-configurator__summary-body';
          const title = document.createElement('span');
          title.className = 'modell-configurator__summary-title';
          title.textContent = line.title || '';
          body.appendChild(title);
          if (line.parts.length) {
            const meta = document.createElement('span');
            meta.className = 'modell-configurator__summary-meta';
            this.renderMetaInto(meta, line.parts);
            body.appendChild(meta);
          }
          row.appendChild(body);
          if (line.priceText) {
            const priceEl = document.createElement('span');
            priceEl.className = 'modell-configurator__summary-price';
            priceEl.textContent = line.priceText;
            row.appendChild(priceEl);
          }
          section.appendChild(row);
        });

        container.appendChild(section);
      });

      if (this.totalEl && this.totalValueEl) {
        if (hasPriced) {
          this.totalValueEl.textContent = this.formatMoney(total);
          this.totalEl.hidden = false;
        } else {
          this.totalEl.hidden = true;
        }
      }

      // The loyalty pill below the add-to-cart button earns points on the
      // CONFIGURED total, not on any single product's price - it is rendered
      // with price 0 and fed from here on every summary update.
      const bonusPoints = this.querySelector('bonus-points-latori');
      if (bonusPoints) {
        bonusPoints.dataset.price = String(hasPriced ? total : 0);
        if (typeof bonusPoints.recalculate === 'function') {
          bonusPoints.recalculate();
        }
      }
    }

    /** Renders "a | b | c" meta text with styled separators into a node. */
    renderMetaInto(container, parts) {
      parts.forEach((text, index) => {
        if (index > 0) {
          const sep = document.createElement('span');
          sep.className = 'modell-configurator__group-title-sep';
          sep.textContent = '|';
          container.appendChild(sep);
          container.appendChild(document.createTextNode(' ' + text));
        } else {
          container.appendChild(document.createTextNode(text));
        }
      });
    }

    /** Formats integer cents in the currency of the active market, e.g. 175 -> "1,75 €" or "1,75 CHF". */
    formatMoney(cents) {
      const amount = Number(cents) / 100;
      const currency =
        (this.summaryContainer && this.summaryContainer.dataset.currency) ||
        'EUR';
      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'de',
          { style: 'currency', currency },
        ).format(amount);
      } catch (error) {
        return amount.toFixed(2) + ' ' + currency;
      }
    }

    /**
     * Flattens the current configuration into /cart/add.js line items:
     * every checked Farbe color at its resolved quantity, the Anleitung
     * (if one is selected), and each included Zubehör accessory.
     */
    collectLineItems() {
      this.readStateFromDom();
      const items = [];

      this.state.colors.forEach((color) => {
        const qty = this.resolveColorQty(color, this.state.size);
        if (color.variantId && qty > 0) {
          items.push({ id: Number(color.variantId), quantity: qty });
        }
      });

      if (this.state.instruction && this.state.instruction.variantId) {
        items.push({
          id: Number(this.state.instruction.variantId),
          quantity: 1,
        });
      }

      this.state.accessories
        .filter(
          (accessory) =>
            accessory.included && accessory.variantId && accessory.qty > 0,
        )
        .forEach((accessory) => {
          items.push({
            id: Number(accessory.variantId),
            quantity: accessory.qty,
          });
        });

      return items;
    }

    /**
     * `_connector_id` values already used by line items in the cart. The
     * property groups one configurator run's items together, so a new run
     * must not reuse an id that's still in the cart.
     */
    async fetchExistingConnectorIds() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return [];
        const cart = await response.json();
        return cart.items
          .map((item) => item.properties && item.properties._connector_id)
          .filter(Boolean)
          .map(String);
      } catch (error) {
        return [];
      }
    }

    /**
     * A short, human-readable id (4 digits) shared by all line items of
     * one configurator run. Only has to be unique within the current cart
     * - collisions with the handful of ids already in it are re-rolled.
     */
    generateConnectorId(existingIds) {
      const taken = new Set(existingIds);
      let id;
      do {
        id = String(Math.floor(1000 + Math.random() * 9000));
      } while (taken.has(id));
      return id;
    }

    /**
     * The model product's variant the configuration belongs to - the
     * checked Groesse tile when the model has a size step (its tiles ARE
     * the model product's variants), else the PDP's selected/first
     * available variant stamped on the element. Written onto every line
     * item as `_parent_variant_id` so a cart-transform Function can merge
     * the group under this variant.
     */
    resolveParentVariantId() {
      const checkedSize = this.querySelector(
        '[data-modell-configurator-size]:checked',
      );
      return (
        (checkedSize && checkedSize.dataset.variantId) ||
        this.dataset.parentVariantId ||
        ''
      );
    }

    /** Shows/clears the summary step's add-to-cart error message. */
    setSubmitError(message) {
      if (!this.submitError) return;
      this.submitError.textContent = message || '';
      this.submitError.hidden = !message;
    }

    /**
     * Adds the whole configuration to the cart in one /cart/add.js call.
     * Every line item carries the same hidden `_connector_id` property
     * (underscore prefix: the theme's cart templates skip it) so the
     * items of one configurator run stay identifiable as a group. The
     * post-add flow mirrors main.js's ProductForm.handleSubmit: bundled
     * section rendering for page-header + cart-drawer, then the drawer
     * opens (per the store's after-add-to-cart setting).
     */
    async handleSubmit() {
      if (
        !this.submitButton ||
        this.submitButton.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      const items = this.collectLineItems();
      if (!items.length) return;

      this.setSubmitError('');
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('is-loading');

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el);

      try {
        const connectorId = this.generateConnectorId(
          await this.fetchExistingConnectorIds(),
        );
        const parentVariantId = this.resolveParentVariantId();
        const properties = { _connector_id: connectorId };
        if (parentVariantId) {
          properties._parent_variant_id = parentVariantId;
        }

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: items.map((item) => ({
              ...item,
              properties,
            })),
            sections: sectionsToUpdate
              .map((el) => el.dataset.sectionId)
              .join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (!response.ok) {
          let error =
            typeof data.description === 'string'
              ? data.description
              : data.message;
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors)
              .map((entry) => entry[1].join(', '))
              .join(', ');
          }
          this.setSubmitError(
            error ||
              (this.submitError && this.submitError.dataset.genericError) ||
              '',
          );
          throw new Error(response.status);
        }

        if (
          data.sections &&
          sectionsToUpdate.length === Object.keys(data.sections).length
        ) {
          sectionsToUpdate.forEach((el) => {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          });
        } else {
          this.dispatchEvent(
            new CustomEvent('on:cart:change', { bubbles: true }),
          );
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));

        if (theme.settings.afterAddToCart === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          const drawer = document.querySelector('.js-cart-drawer');
          if (drawer) drawer.open(this.submitButton);
        }

        this.submitButton.classList.add('is-success');
        this.submitButton.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitButton.classList.remove('is-loading');
          this.submitButton.classList.remove('is-success');
        }, 2000);
      } catch (error) {
        if (this.submitError && this.submitError.hidden) {
          this.setSubmitError(this.submitError.dataset.genericError || '');
        }
        // The cart may have changed despite the error.
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        this.submitButton.classList.remove('is-loading');
        this.submitButton.classList.remove('is-success');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    renderStep() {
      this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;

      this.steps.forEach((panel, index) => {
        const isActive = index === this.currentIndex;
        panel.classList.toggle('is-active', isActive);
        panel.toggleAttribute('inert', !isActive);
      });

      this.stepperItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === this.currentIndex);
        item.classList.toggle('is-complete', index < this.currentIndex);
        item.classList.toggle('is-locked', index > this.maxUnlockedIndex);
      });

      this.syncViewportHeight();

      // Entering a step must show the pages holding the current
      // selections - the customer may have paged a swatch carousel away
      // from their checked color while browsing before moving on. No-op
      // for carousels whose Swiper isn't initialized (yet); init() does
      // its own initial reveal.
      const activePanel = this.steps[this.currentIndex];
      if (activePanel) {
        activePanel
          .querySelectorAll('swatch-carousel-latori')
          .forEach((carousel) => carousel.revealCheckedSlide?.(0));
      }

      this.syncGalleryPreview();
    }

    /**
     * Shows the colour that the customer clicked in the Farbe step as the
     * big image of the PDP gallery while the Farbe or Größe step is open.
     * Other steps and a closed configurator show the model photo again.
     * Nothing changes before the customer clicks a colour, because the model
     * photo already shows the preselected colours.
     *
     * The colour image goes into a hidden slot in front of the model photo
     * (data-media-id "color-preview-latori", rendered by main-product.liquid
     * for model products). Thus the model photo stays in the gallery as the
     * second image, and the customer can select it again with the thumbnail
     * or the arrows (client request 2026-10-06). The slot shows and hides
     * with the hidden attribute, the same as variant-media-latori.js does.
     */
    syncGalleryPreview() {
      const taxonomy = this.steps[this.currentIndex]?.dataset.stepTaxonomy;
      const show =
        !!this.galleryPreviewSrc &&
        this.classList.contains('is-active') &&
        (taxonomy === 'farbe' || taxonomy === 'groesse');
      const src = show ? this.galleryPreviewSrc : null;
      if (src === (this.galleryPreviewShown || null)) return;
      this.galleryPreviewShown = src;

      const gallery =
        this.closest('.product-info')?.querySelector('media-gallery') ||
        document.querySelector('media-gallery');
      if (!gallery) return;
      const previewId = 'color-preview-latori';
      const slots = gallery.querySelectorAll(`[data-media-id="${previewId}"]`);
      if (!slots.length) return;

      slots.forEach((slot) => {
        if (src) {
          slot.querySelectorAll('img').forEach((img) => {
            img.removeAttribute('srcset');
            img.src = src;
          });
        }
        slot.hidden = !src;
      });

      // Same as variant-media-latori.js: refresh the sliders that are on the
      // screen, so that they include or exclude the slot.
      gallery.querySelectorAll('carousel-slider').forEach((slider) => {
        if (slider.offsetParent && typeof slider.refresh === 'function') {
          slider.refresh();
        }
      });

      if (typeof gallery.setActiveMedia !== 'function') return;
      if (src) {
        gallery.setActiveMedia(previewId, true, true);
        return;
      }
      const modelPhoto = gallery.querySelector(
        `.main-image .slider__item:not([hidden]):not([data-media-id="${previewId}"])`,
      );
      if (modelPhoto) gallery.setActiveMedia(modelPhoto.dataset.mediaId, true, true);
    }

    /**
     * Keeps the sliding viewport exactly as tall as the ACTIVE step panel.
     * The track lays every step panel side by side, so its natural height
     * is the TALLEST panel's (usually Farbe with its swatch carousels) -
     * short steps like Größe would otherwise sit above a huge blank area
     * pushing everything below the section down. Panels are top-aligned
     * (align-items: flex-start on the track) so each keeps its natural
     * height to measure. A ResizeObserver re-syncs while the active
     * panel's own content grows or shrinks (quantity rows render
     * client-side, validation errors appear, carousels initialize).
     * Because every step change first scrolls back to the section start
     * and only then swaps panels, the height change happens below the
     * fold - no visible layout jump for the customer.
     */
    syncViewportHeight() {
      if (!this.viewport) return;
      const active = this.steps[this.currentIndex];
      if (!active) return;

      if (!this.viewportHeightObserver) {
        this.viewportHeightObserver = new ResizeObserver(() =>
          this.applyViewportHeight(),
        );
      }
      this.viewportHeightObserver.disconnect();
      this.viewportHeightObserver.observe(active);

      // The ResizeObserver alone is NOT enough: its callbacks are delivered
      // with the rendering frames, which browsers pause entirely for
      // occluded/background windows - exactly the situation in which the
      // lazily-initialized swatch carousels tend to finish loading (their
      // skeleton is swapped for the real, differently-tall content while
      // the customer is on another tab/window). A stale height then clips
      // the step's footer buttons at the bottom. MutationObserver callbacks
      // are microtasks and keep firing regardless of visibility, so DOM-
      // driven height changes (carousel [loaded]/skeleton removal, quantity
      // rows, sold-out notices, validation errors) always re-sync; the
      // ResizeObserver still covers pure layout resizes (viewport width,
      // fonts) while visible.
      if (!this.panelMutationObserver) {
        this.panelMutationObserver = new MutationObserver(() =>
          this.applyViewportHeight(),
        );
      }
      this.panelMutationObserver.disconnect();
      this.panelMutationObserver.observe(active, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['hidden', 'loaded', 'inactive', 'class', 'style'],
      });

      this.applyViewportHeight();
    }

    /**
     * Writes the active panel's current height onto the viewport, plus a
     * 2px buffer: offsetHeight rounds a fractional layout height DOWN, and
     * with overflow:hidden that sliver shaved the footer buttons' bottom
     * edge off.
     */
    applyViewportHeight() {
      if (!this.viewport) return;
      const panel = this.steps[this.currentIndex];
      if (!panel) return;
      const height = `${panel.offsetHeight + 2}px`;
      if (this.viewport.style.height !== height) {
        this.viewport.style.height = height;
      }
    }
  }

  customElements.define('modell-configurator', ModellConfigurator);
}
